import React, { useState } from 'react';
import { CheckIcon } from '@radix-ui/react-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import {
	Card,
	CardContent,
	Button,
} from '../ui';
import { ScanCreateForm, ScanProgress } from '../composite';
import { PATHS, ROUTE_PATTERNS, buildRoute } from '../../constants/Paths';

/**
 * ScanCreate - Page component for creating new scans
 * 
 * Features:
 * - Multi-step scan creation flow
 * - Progress tracking
 * - Proper client/account selection
 * - Error handling and validation
 * - Navigation to results
 */
export const ScanCreatePage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const [currentStep, setCurrentStep] = useState("form"); // 'form', 'progress', 'completed'
	const [scanResult, setScanResult] = useState(null);
	const [scanId, setScanId] = useState(null);

	// Extract pre-filled data from navigation state
	const initialData = location.state || {};

	const handleScanCreated = (result) => {
		setScanResult(result);
		const scanId = result.data?.scanId || result.scanId;
		setScanId(scanId);
		setCurrentStep("progress");
	};

	// No auto-navigate: the completion summary used to yank the user to the
	// report after a hard-coded 3 s, before it could be read. The summary has a
	// "View report" button - moving on is the user's choice.
	const handleScanComplete = (finalResult) => {
		setScanResult(finalResult);
		setCurrentStep("completed");
	};

	const handleCancel = () => {
		navigate(PATHS.SCAN_LIST);
	};

	const handleStartNewScan = () => {
		setCurrentStep("form");
		setScanResult(null);
		setScanId(null);
	};

	const getStepIcon = (step, currentStep) => {
		if (getStepNumber(step) < getStepNumber(currentStep)) {
			return <CheckIcon className="h-4 w-4 text-white" />;
		}
		return (
			<span className={currentStep === step ? 'text-white font-medium' : 'text-muted-foreground font-medium'}>
				{getStepNumber(step)}
			</span>
		);
	};

	const getStepNumber = (step) => {
		const steps = { form: 1, progress: 2, completed: 3 };
		return steps[step];
	};

	const getStepClasses = (step, currentStep) => {
		if (currentStep === step) {
			return "bg-brand-600 text-white";
		} else if (getStepNumber(step) < getStepNumber(currentStep)) {
			return "bg-brand-600 text-white";
		} else {
			return "bg-muted text-muted-foreground";
		}
	};

	return (
		<div className="min-h-screen bg-muted">
			<div className="max-w-7xl mx-auto p-6">
				{/* Header */}
				<div className="flex items-center justify-between mb-6">
					<div>
						<button
							onClick={() => navigate(PATHS.SCAN_LIST)}
							className="text-muted-foreground hover:text-foreground mb-2 flex items-center gap-2"
						>
							← Back to Scans
						</button>
						<h1 className="text-3xl font-bold text-foreground">Create New Scan</h1>
						<p className="text-muted-foreground mt-1">
							Multi-platform social media monitoring with AI-powered risk analysis
						</p>
					</div>
					<Button variant="outline" onClick={handleCancel}>
						Cancel
					</Button>
				</div>

				{/* Progress Steps */}
				<Card className="mb-8">
					<CardContent className="p-6">
						<div className="flex items-center justify-center">
							<div className="flex items-center space-x-8">
								{/* Step 1 */}
								<div className="flex items-center">
									<div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${getStepClasses("form", currentStep)}`}>
										{getStepIcon("form", currentStep)}
									</div>
									<span className="ml-3 text-sm font-medium text-foreground">Configure Scan</span>
								</div>

								{/* Arrow */}
								<svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
								</svg>

								{/* Step 2 */}
								<div className="flex items-center">
									<div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${getStepClasses("progress", currentStep)}`}>
										{getStepIcon("progress", currentStep)}
									</div>
									<span className="ml-3 text-sm font-medium text-foreground">Monitor Progress</span>
								</div>

								{/* Arrow */}
								<svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
								</svg>

								{/* Step 3 */}
								<div className="flex items-center">
									<div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${getStepClasses("completed", currentStep)}`}>
										{getStepIcon("completed", currentStep)}
									</div>
									<span className="ml-3 text-sm font-medium text-foreground">Review Results</span>
								</div>
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Content */}
				<div className="space-y-6">
					{currentStep === "form" && (
						<ScanCreateForm
							initialData={initialData}
							onScanCreated={handleScanCreated}
							onCancel={handleCancel}
						/>
					)}

					{currentStep === "progress" && (
						<Card>
							<CardContent className="p-6">
								<ScanProgress
									scanId={scanId}
									onComplete={handleScanComplete}
								/>

								{/* Progress Actions */}
								<div className="flex justify-center space-x-4 mt-6">
									<Button variant="outline" onClick={handleCancel}>
										Cancel Scan
									</Button>
									<Button onClick={() => navigate(PATHS.SCAN_LIST)}>
										View All Scans
									</Button>
								</div>
							</CardContent>
						</Card>
					)}

					{currentStep === "completed" && scanResult && (
						<Card>
							<CardContent className="p-6 space-y-6">
								{/* Completion Message */}
								<div className="text-center">
									<div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4 dark:bg-green-500/15">
										<svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
										</svg>
									</div>
									<h3 className="text-lg font-medium text-foreground mb-2">Scan complete</h3>
									<p className="text-sm text-muted-foreground">
										Every post in the selected window has been collected and analyzed.
									</p>
								</div>

								{/* Quick Results Summary */}
								<div className="bg-muted rounded-lg p-6">
									<h3 className="text-lg font-medium text-foreground mb-4">Quick Summary</h3>
									<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
										<div className="text-center">
											<div className="text-2xl font-bold text-brand-600">
												{scanResult.results?.totalPosts || 0}
											</div>
											<div className="text-sm text-muted-foreground">Total Posts</div>
										</div>
										<div className="text-center">
											<div className="text-2xl font-bold text-green-600">
												{scanResult.results?.analyzedPosts || 0}
											</div>
											<div className="text-sm text-muted-foreground">Analyzed</div>
										</div>
										<div className="text-center">
											<div className="text-2xl font-bold text-red-600">
												{scanResult.results?.flaggedPosts || 0}
											</div>
											<div className="text-sm text-muted-foreground">Flagged</div>
										</div>
										<div className="text-center">
											<div className="text-2xl font-bold text-orange-600">
												{scanResult.results?.deletedPosts || 0}
											</div>
											<div className="text-sm text-muted-foreground">Deleted</div>
										</div>
									</div>
								</div>

								{/* Actions */}
								<div className="flex justify-center space-x-4">
									<Button variant="outline" onClick={handleStartNewScan}>
										Start New Scan
									</Button>
									<Button onClick={() => navigate(buildRoute(ROUTE_PATTERNS.SCAN_DETAIL, { id: scanResult.data?.scanId || scanResult.scanId }))}>
										View Full Results
									</Button>
								</div>
							</CardContent>
						</Card>
					)}
				</div>

			</div>
		</div>
	);
}; 